import type { AxiosInstance } from 'axios'
import type {
  ApiKey,
  ApiKeyEnvironment,
  ApiKeySecret,
  ApiKeyStatus,
  ApiKeyUsage,
  ApiKeysClient,
  CreateApiKeyPayload,
  UpdateApiKeyPayload
} from './apiKeys.types'
import { maskSecret } from './format'
import { expirationToSeconds } from './scopeCatalog'
import { errorStatus } from '../http/errorStatus'
import { toUsageSummary } from '../usage/usage.types'
import type { UsageSummaryDto } from '../usage/usage.types'

/**
 * HTTP contract for API key management in the Developer Platform
 * (SAIV-11350), following the backend's `integrations/v1` routes:
 *
 *   GET/POST        integrations/v1/orgs/{org_id}/api-keys
 *   GET/PATCH/DELETE integrations/v1/orgs/{org_id}/api-keys/{api_key_id}
 *   POST            integrations/v1/orgs/{org_id}/api-keys/{api_key_id}/rotate
 *   GET             integrations/v1/orgs/{org_id}/api-keys/{api_key_id}/usage
 *   GET             integrations/v1/orgs/{org_id}/scopes
 *
 * The wire shape (`ApiKeyDto`) mirrors `IntegrationApiKeyRead` from the
 * backend OpenAPI spec; the mappers below are the only place that knows
 * about it. Set `VITE_DEVELOPER_PLATFORM_MOCK_API=true` to run the UI
 * against the in-memory client instead.
 */
export function apiKeysPath(orgId: string): string {
  return `integrations/v1/orgs/${encodeURIComponent(orgId)}/api-keys`
}

export function scopesPath(orgId: string): string {
  return `integrations/v1/orgs/${encodeURIComponent(orgId)}/scopes`
}

/** Largest `page_size` the list endpoint accepts. */
const LIST_PAGE_SIZE = 200
export const USAGE_PERIOD_DAYS = 30

/** `IntegrationApiKeyRead` */
export interface ApiKeyDto {
  id: number
  name: string
  masked_key?: string | null
  environment: ApiKeyEnvironment
  status: ApiKeyStatus
  scopes: string[]
  facility_ids?: number[] | null
  expires_at?: string | null
  expires_in_seconds?: number | null
  issued_at: string
  last_used_at?: string | null
  revoked_at?: string | null
  created_at?: string | null
  last_modified_at?: string | null
  created_by?: string | null
  last_modified_by?: string | null
}

/** `IntegrationApiKeyCreatedRead`: the record plus the one-time secret. */
export interface ApiKeyCreatedDto extends ApiKeyDto {
  api_key: string
}

/** `IntegrationApiKeyListRead` */
export interface ApiKeyListDto {
  items: ApiKeyDto[]
  total: number
  page: number
  page_size: number
}

/** `IntegrationApiKeyCreate` */
interface CreateApiKeyDto {
  name: string
  environment: ApiKeyEnvironment
  scopes: string[]
  expires_in_seconds: number | null
}

/** `IntegrationApiKeyUpdate`; omitted fields are left untouched. */
interface UpdateApiKeyDto {
  name?: string
  scopes?: string[]
  status?: ApiKeyStatus
  expires_in_seconds?: number | null
}

export function toApiKey(dto: ApiKeyDto, secret?: string): ApiKey {
  return {
    id: String(dto.id),
    name: dto.name,
    maskedKey: dto.masked_key ?? (secret ? maskSecret(secret) : ''),
    environment: dto.environment,
    status: dto.status,
    scopes: dto.scopes ?? [],
    facilityIds: dto.facility_ids ?? null,
    createdAt: dto.created_at ?? dto.issued_at,
    issuedAt: dto.issued_at,
    createdBy: { name: dto.created_by ?? '' },
    lastUsedAt: dto.last_used_at ?? null,
    expiresAt: dto.expires_at ?? null,
    revokedAt: dto.revoked_at ?? null
  }
}

export function toApiKeySecret(dto: ApiKeyCreatedDto): ApiKeySecret {
  return { key: toApiKey(dto, dto.api_key), secret: dto.api_key }
}

/** HTTP statuses that mean "this backend does not serve usage yet". */
const UNAVAILABLE_STATUSES = new Set([404, 501])

export class HttpApiKeysClient implements ApiKeysClient {
  private readonly base: string

  constructor(
    private readonly apiClient: AxiosInstance,
    orgId: string
  ) {
    this.base = apiKeysPath(orgId)
  }

  private keyPath(id: string): string {
    return `${this.base}/${encodeURIComponent(id)}`
  }

  /** Every key of the organisation, following pagination when there are more than one page. */
  async list(): Promise<ApiKey[]> {
    const items: ApiKeyDto[] = []
    let page = 1
    for (;;) {
      const { data } = await this.apiClient.get<ApiKeyListDto>(this.base, {
        params: { page, page_size: LIST_PAGE_SIZE }
      })
      items.push(...data.items)
      if (data.items.length === 0 || items.length >= data.total) break
      page += 1
    }
    return items.map(key => toApiKey(key))
  }

  async get(id: string): Promise<ApiKey | null> {
    try {
      const { data } = await this.apiClient.get<ApiKeyDto>(this.keyPath(id))
      return toApiKey(data)
    } catch (error) {
      if (errorStatus(error) === 404) return null
      throw error
    }
  }

  async create(payload: CreateApiKeyPayload): Promise<ApiKeySecret> {
    const body: CreateApiKeyDto = {
      name: payload.name,
      environment: payload.environment,
      scopes: payload.scopes,
      expires_in_seconds: expirationToSeconds(payload.expiration)
    }
    const { data } = await this.apiClient.post<ApiKeyCreatedDto>(this.base, body)
    return toApiKeySecret(data)
  }

  async update(id: string, payload: UpdateApiKeyPayload): Promise<ApiKey> {
    const body: UpdateApiKeyDto = {}
    if (payload.name !== undefined) body.name = payload.name
    if (payload.scopes !== undefined) body.scopes = payload.scopes
    // `never` is sent as an explicit null so the backend clears the expiry.
    // The backend anchors a new lifetime on the key's issued_at, so the
    // seconds are computed from that anchor to keep "30 days" meaning 30
    // days from today.
    if (payload.expiration !== undefined) {
      const now = new Date()
      const anchor = payload.issuedAt ? new Date(payload.issuedAt) : now
      body.expires_in_seconds = expirationToSeconds(payload.expiration, now, anchor)
    }
    const { data } = await this.apiClient.patch<ApiKeyDto>(this.keyPath(id), body)
    return toApiKey(data)
  }

  async rotate(id: string): Promise<ApiKeySecret> {
    const { data } = await this.apiClient.post<ApiKeyCreatedDto>(`${this.keyPath(id)}/rotate`)
    return toApiKeySecret(data)
  }

  async setEnabled(id: string, enabled: boolean): Promise<ApiKey> {
    const body: UpdateApiKeyDto = { status: enabled ? 'active' : 'inactive' }
    const { data } = await this.apiClient.patch<ApiKeyDto>(this.keyPath(id), body)
    return toApiKey(data)
  }

  async remove(id: string): Promise<void> {
    await this.apiClient.delete(this.keyPath(id))
  }

  async getUsage(id: string): Promise<ApiKeyUsage | null> {
    try {
      const { data } = await this.apiClient.get<UsageSummaryDto>(`${this.keyPath(id)}/usage`, {
        params: { period_days: USAGE_PERIOD_DAYS }
      })
      return toUsageSummary(data)
    } catch (error) {
      if (UNAVAILABLE_STATUSES.has(errorStatus(error) ?? -1)) return null
      throw error
    }
  }
}
